import type { Payout } from "@biteasy/contracts";
import {
	skipToken,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Banknote, Download, EllipsisVertical, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CopyableId } from "@/components/CopyableId";
import { Badge } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import {
	Dropdown,
	DropdownButton,
	DropdownItem,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import { Heading } from "@/components/catalyst/Heading";
import {
	Pagination,
	PaginationNext,
	PaginationPrevious,
} from "@/components/catalyst/Pagination";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/catalyst/Table";
import { Time } from "@/components/Time";
import { orpc } from "@/orpc/client";
import { downloadCSV, formatCurrency, generateCSV } from "@/util/format";
import { type PayoutsFilters, PayoutsFiltersBar } from "./PayoutsFiltersBar";

const PAYOUT_STATUS_CONFIG = {
	paid: { color: "emerald", label: "Paid" },
	pending: { color: "amber", label: "Pending" },
	failed: { color: "rose", label: "Failed" },
	canceled: { color: "zinc", label: "Canceled" },
	needs_attention: { color: "orange", label: "Needs Attention" },
} as const;

/**
 * Generate CSV content from payouts data
 */
function generatePayoutsCSV(items: Payout[]): string {
	const headers = [
		"ID",
		"Partner",
		"Amount",
		"Currency",
		"Status",
		"Stripe Transfer ID",
		"Created At",
		"Paid At",
	];

	const rows = items.map((item) => [
		item.id,
		item.partnerName,
		(item.amountCents / 100).toFixed(2),
		item.status,
		item.stripeTransferId ?? "",
		item.createdAt.toISOString(),
		item.paidAt?.toISOString() ?? "",
	]);

	return generateCSV(headers, rows);
}

export function Payouts() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { pc: cursors } = useSearch({ from: "/_app/finances/" });
	const setCursors = useCallback(
		(updater: string[] | ((prev: string[]) => string[])) => {
			navigate({
				to: "/finances",
				search: (prev) => ({
					...prev,
					tab: prev.tab as "earnings" | "payouts" | "payment_methods",
					pc:
						typeof updater === "function"
							? updater((prev.pc as string[]) ?? [])
							: updater,
				}),
			});
		},
		[navigate],
	);
	const [filters, setFilters] = useState<PayoutsFilters>({});
	const [isExporting, setIsExporting] = useState(false);
	const [exportComplete, setExportComplete] = useState(false);

	const currentCursor =
		cursors.length > 0 ? cursors[cursors.length - 1] : undefined;

	// Track which payout is currently being retried
	const [retryingPayoutId, setRetryingPayoutId] = useState<string | null>(null);

	// Retry mutation for charge_failed payouts
	const retryMutation = useMutation(
		orpc.payouts.retry.mutationOptions({
			onSuccess: (result) => {
				queryClient.invalidateQueries({
					queryKey: orpc.payouts.list.key(),
				});
				toast.success(result.message);
				setRetryingPayoutId(null);
			},
			onError: (err) => {
				toast.error(`Retry failed: ${err.message}`);
				setRetryingPayoutId(null);
			},
		}),
	);

	// Cancel and retry mutation for awaiting_action payouts
	const cancelAndRetryMutation = useMutation(
		orpc.payouts.cancelAndRetry.mutationOptions({
			onSuccess: (result) => {
				queryClient.invalidateQueries({ queryKey: orpc.payouts.list.key() });
				toast.success(result.message);
				setRetryingPayoutId(null);
			},
			onError: (err) => {
				toast.error(`Cancel failed: ${err.message}`);
				setRetryingPayoutId(null);
			},
		}),
	);

	const { data } = useSuspenseQuery(
		orpc.payouts.list.queryOptions({
			input: {
				limit: 20,
				cursor: currentCursor,
				partnerId: filters.partnerId,
				status: filters.status,
				amountMin: filters.amountMin,
				amountMax: filters.amountMax,
				createdAfter: filters.createdAfter,
				createdBefore: filters.createdBefore,
				paidAfter: filters.paidAfter,
				paidBefore: filters.paidBefore,
			},
		}),
	);

	// Streaming export query using oRPC's streamedOptions
	const exportQuery = useQuery(
		orpc.payouts.export.experimental_streamedOptions({
			input: isExporting
				? {
						partnerId: filters.partnerId,
						status: filters.status,
						amountMin: filters.amountMin,
						amountMax: filters.amountMax,
						createdAfter: filters.createdAfter,
						createdBefore: filters.createdBefore,
						paidAfter: filters.paidAfter,
						paidBefore: filters.paidBefore,
					}
				: skipToken,
			queryFnOptions: {
				refetchMode: "reset",
			},
			retry: true,
		}),
	);

	// Handle export completion
	useEffect(() => {
		if (
			isExporting &&
			exportQuery.data &&
			exportQuery.fetchStatus === "idle" &&
			!exportQuery.isFetching &&
			!exportComplete
		) {
			const items = exportQuery.data;
			const csv = generatePayoutsCSV(items);
			const timestamp = new Date().toISOString().split("T")[0];
			downloadCSV(csv, `payouts-export-${timestamp}.csv`);
			toast.success(`Exported ${items.length} payouts`);
			setIsExporting(false);
			setExportComplete(true);
		}
	}, [
		isExporting,
		exportQuery.data,
		exportQuery.fetchStatus,
		exportQuery.isFetching,
		exportComplete,
	]);

	const handleExport = () => {
		setExportComplete(false);
		setIsExporting(true);
	};

	const handleFiltersChange = useCallback(
		(newFilters: PayoutsFilters) => {
			setFilters(newFilters);
			// Reset pagination when filters change
			setCursors([]);
			// Reset export state when filters change
			setExportComplete(false);
		},
		[setCursors],
	);

	const handleNext = () => {
		if (data.nextCursor) {
			setCursors((prev) => [...prev, data.nextCursor ?? ""]);
		}
	};

	const handlePrevious = () => {
		if (cursors.length > 0) {
			setCursors((prev) => prev.slice(0, -1));
		}
	};

	const hasPrevious = cursors.length > 0;
	const hasNext = !!data.nextCursor;

	return (
		<div>
			<div className="flex items-center justify-between">
				<Heading level={2}>Partner Payouts</Heading>
				<Button
					outline
					onClick={handleExport}
					disabled={isExporting || exportQuery.isFetching}
				>
					<Download className="size-4 mr-1" />
					{isExporting || exportQuery.isFetching
						? `Exporting${exportQuery.data ? ` (${exportQuery.data.length})` : "..."}`
						: "Export CSV"}
				</Button>
			</div>

			<PayoutsFiltersBar onFiltersChange={handleFiltersChange} />

			{data.totalCount > 0 && (
				<p className="mt-4 text-sm text-base-content/60 text-right">
					{data.totalCount.toLocaleString()} payout
					{data.totalCount !== 1 ? "s" : ""} recorded
				</p>
			)}

			{data.items.length === 0 ? (
				<div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50 px-6 py-16">
					<div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
						<Banknote className="size-8 text-primary" />
					</div>
					<h3 className="mt-4 text-lg font-semibold text-base-content">
						No payouts found
					</h3>
					<p className="mt-2 max-w-sm text-center text-sm text-base-content/60">
						{Object.keys(filters).length > 0
							? "Try adjusting your filters to see more results."
							: "Payouts to your partners will appear here once earnings are transferred via Stripe."}
					</p>
				</div>
			) : (
				<>
					<Table className="mt-8">
						<TableHead>
							<TableRow>
								<TableHeader>Partner</TableHeader>
								<TableHeader>Amount</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Stripe Transfer</TableHeader>
								<TableHeader>Created</TableHeader>
								<TableHeader>Paid</TableHeader>
								<TableHeader>
									<span className="sr-only">Actions</span>
								</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.items.map((payout) => {
								const statusConfig = PAYOUT_STATUS_CONFIG[payout.status] ?? {
									color: "zinc",
									label: payout.status,
								};
								const canRetry =
									payout.internalStatus === "charge_failed" ||
									payout.internalStatus === "awaiting_action";

								return (
									<TableRow key={payout.id}>
										<TableCell className="font-medium text-base-content">
											{payout.partnerName}
										</TableCell>
										<TableCell className="font-medium text-base-content">
											{formatCurrency(payout.amountCents, "USD")}
										</TableCell>
										<TableCell>
											<Badge color={statusConfig.color}>
												{statusConfig.label}
											</Badge>
										</TableCell>
										<TableCell className="text-base-content/60">
											{payout.stripeTransferId ? (
												<CopyableId value={payout.stripeTransferId} />
											) : (
												"—"
											)}
										</TableCell>
										<TableCell className="text-base-content/60">
											<Time date={payout.createdAt} />
										</TableCell>
										<TableCell className="text-base-content/60">
											{payout.paidAt ? <Time date={payout.paidAt} /> : "—"}
										</TableCell>
										<TableCell>
											{canRetry && (
												<div className="flex items-center justify-end">
													<Dropdown>
														<DropdownButton
															plain
															aria-label="More options"
															disabled={retryingPayoutId === payout.id}
														>
															{retryingPayoutId === payout.id ? (
																<RefreshCw className="size-5 animate-spin" />
															) : (
																<EllipsisVertical className="size-5" />
															)}
														</DropdownButton>
														<DropdownMenu anchor="bottom end">
															{payout.internalStatus === "charge_failed" && (
																<DropdownItem
																	onClick={() => {
																		setRetryingPayoutId(payout.id);
																		retryMutation.mutate({ id: payout.id });
																	}}
																	className="gap-2"
																>
																	<RefreshCw className="size-4" />
																	Retry Payout
																</DropdownItem>
															)}
															{payout.internalStatus === "awaiting_action" && (
																<DropdownItem
																	onClick={() => {
																		setRetryingPayoutId(payout.id);
																		cancelAndRetryMutation.mutate({
																			id: payout.id,
																			createNewPayout: true,
																		});
																	}}
																>
																	<RefreshCw className="size-4" />
																	Use Different Card
																</DropdownItem>
															)}
														</DropdownMenu>
													</Dropdown>
												</div>
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					<Pagination className="mt-4">
						<PaginationPrevious
							onClick={hasPrevious ? handlePrevious : undefined}
							className={
								!hasPrevious
									? "pointer-events-none opacity-50"
									: "cursor-pointer!"
							}
							href={hasPrevious ? undefined : "#"}
						/>
						<PaginationNext
							onClick={hasNext ? handleNext : undefined}
							className={
								!hasNext ? "pointer-events-none opacity-50" : "cursor-pointer!"
							}
							href={hasNext ? undefined : "#"}
						/>
					</Pagination>
				</>
			)}
		</div>
	);
}
